import React, { useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Card } from "@design-system/Card/Card";
import { VStack } from "@design-system/VStack/VStack";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { SandboxPanelContext } from "./sandboxPanelContext";
import styles from "./SandboxLayout.module.scss";

/**
 * @purpose Shell for component sandboxes — the live preview fills the main
 * column while the configurator sits in the app's shared details side panel.
 * The panel starts open whenever a showcase provides `controls`; hiding it via
 * the preview header releases the space to the stage.
 */
export const SandboxLayout = ({
    controls,
    children,
}: {
    controls?: React.ReactNode;
    children: React.ReactNode;
}) => {
    const isNarrow = useMediaQuery({ maxWidth: 900 });
    const sandboxRef = useRef<HTMLDivElement | null>(null);
    const { topOffset, hasMeasured } = useDynamicTopOffset({
        containerRef: sandboxRef,
    });

    const hasControls = controls !== undefined && controls !== null;
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const panelState = useMemo(
        () => ({
            hasControls,
            isPanelOpen,
            togglePanel: () => setIsPanelOpen((isOpen) => !isOpen),
        }),
        [hasControls, isPanelOpen],
    );

    const fillHeight =
        !isNarrow && hasMeasured ? `calc(100vh - ${topOffset}px)` : undefined;

    return (
        <SandboxPanelContext.Provider value={panelState}>
            <div
                ref={sandboxRef}
                className={styles.sandbox}
                style={fillHeight ? { minHeight: fillHeight } : undefined}
            >
                <div className={styles.sandboxMain}>{children}</div>
                {hasControls && isPanelOpen ? (
                    <Card
                        className={styles.aside}
                        sharpEdges
                        style={
                            fillHeight
                                ? {
                                      minHeight: fillHeight,
                                      maxHeight: fillHeight,
                                  }
                                : undefined
                        }
                    >
                        <VStack
                            className={styles.controls}
                            gap={24}
                            padding={24}
                        >
                            {controls}
                        </VStack>
                    </Card>
                ) : null}
            </div>
        </SandboxPanelContext.Provider>
    );
};
